import { type FC } from 'react';
import { AlertTriangle, Mail } from 'lucide-react';
import { Button } from '@/components/ui';

interface LockedItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  itemType: 'tournament' | 'addon';
}

/**
 * Modal that appears when users try to deselect already-purchased items
 * Instructs them to contact support for changes
 */
export const LockedItemModal: FC<LockedItemModalProps> = ({
  isOpen,
  onClose,
  itemName,
  itemType,
}) => {
  if (!isOpen) return null;

  const supportEmail = 'support@cactuscup.com';

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="locked-item-title"
      aria-describedby="locked-item-description"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center" aria-hidden="true">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
          </div>

          <div className="flex-1">
            <h3 id="locked-item-title" className="text-lg font-semibold text-gray-900 mb-2">
              Cannot Remove {itemType === 'tournament' ? 'Tournament' : 'Item'}
            </h3>
            <p id="locked-item-description" className="text-sm text-gray-600 mb-4">
              You've already registered for <span className="font-medium">{itemName}</span>.
              To make changes to your registration, please contact our support team.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 mb-2">Contact Support:</p>
              <a
                href={`mailto:${supportEmail}`}
                className="flex items-center gap-2 text-sm text-turquoise-700 hover:text-turquoise-800 font-medium"
              >
                <Mail className="h-4 w-4" />
                {supportEmail}
              </a>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                onClick={onClose}
                variant="default"
                aria-label="Close dialog"
              >
                Got it
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
